import type { ChangeEvent } from "react";

import type { BlogStructuredContent, BlogStructuredDirection, BlogStructuredIngredientSection, BlogStructuredStat } from "../types";
import { Button, Card, TextField } from "./ui";

interface RecipeStructuredEditorProps {
  body: string;
  onBodyChange: (value: string) => void;
  structuredContent: BlogStructuredContent;
  onChange: (value: BlogStructuredContent) => void;
}

interface MultilineFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  rows?: number;
  onChange: (value: string) => void;
}

function createClientId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeNullableValue(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? value : null;
}

function nextStructuredContent(
  structuredContent: BlogStructuredContent,
  updater: (value: BlogStructuredContent) => BlogStructuredContent,
): BlogStructuredContent {
  const nextValue = updater(structuredContent);
  return {
    ...nextValue,
    displayStyle: "RECIPE",
    stats: nextValue.stats || [],
    ingredients: nextValue.ingredients || [],
    directions: nextValue.directions || [],
    notes: nextValue.notes || [],
  };
}

function MultilineField({ label, value, placeholder, rows = 3, onChange }: MultilineFieldProps) {
  return (
    <label className="flex flex-col gap-2 text-sm">
      <span className="font-medium text-[var(--pr-text-muted)]">{label}</span>
      <textarea
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm outline-none ring-[var(--pr-accent)] transition focus:ring-2"
      />
    </label>
  );
}

function removeArrayIndex<T>(entries: T[], indexToRemove: number): T[] {
  return entries.filter((_, index) => index !== indexToRemove);
}

function updateArrayIndex<T>(entries: T[], indexToUpdate: number, nextValue: T): T[] {
  return entries.map((entry, index) => (index === indexToUpdate ? nextValue : entry));
}

function addEmptyStat(structuredContent: BlogStructuredContent): BlogStructuredContent {
  const nextStat: BlogStructuredStat = {
    id: createClientId("stat"),
    label: "",
    value: "",
    secondaryValue: null,
  };

  return nextStructuredContent(structuredContent, (current) => ({
    ...current,
    stats: [...current.stats, nextStat],
  }));
}

function addEmptyIngredientSection(structuredContent: BlogStructuredContent): BlogStructuredContent {
  const nextSection: BlogStructuredIngredientSection = {
    id: createClientId("ingredients"),
    title: "",
    items: [""],
  };

  return nextStructuredContent(structuredContent, (current) => ({
    ...current,
    ingredients: [...current.ingredients, nextSection],
  }));
}

function addEmptyDirection(structuredContent: BlogStructuredContent): BlogStructuredContent {
  const nextDirection: BlogStructuredDirection = {
    id: createClientId("direction"),
    title: "",
    body: "",
    detail: null,
  };

  return nextStructuredContent(structuredContent, (current) => ({
    ...current,
    directions: [...current.directions, nextDirection],
  }));
}

export function RecipeStructuredEditor({
  body,
  onBodyChange,
  structuredContent,
  onChange,
}: RecipeStructuredEditorProps) {
  function updateTopLevelField(field: "servingText" | "summaryTitle" | "summaryBody", value: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        [field]: normalizeNullableValue(value),
      })),
    );
  }

  function updateStat(statId: string, field: "label" | "value" | "secondaryValue", value: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        stats: current.stats.map((stat) =>
          stat.id === statId
            ? {
                ...stat,
                [field]: field === "secondaryValue" ? normalizeNullableValue(value) : value,
              }
            : stat,
        ),
      })),
    );
  }

  function removeStat(statId: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        stats: current.stats.filter((stat) => stat.id !== statId),
      })),
    );
  }

  function updateIngredientSectionTitle(sectionId: string, value: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        ingredients: current.ingredients.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                title: normalizeNullableValue(value),
              }
            : section,
        ),
      })),
    );
  }

  function updateIngredientItem(sectionId: string, itemIndex: number, value: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        ingredients: current.ingredients.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                items: updateArrayIndex(section.items, itemIndex, value),
              }
            : section,
        ),
      })),
    );
  }

  function addIngredientItem(sectionId: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        ingredients: current.ingredients.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                items: [...section.items, ""],
              }
            : section,
        ),
      })),
    );
  }

  function removeIngredientItem(sectionId: string, itemIndex: number): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        ingredients: current.ingredients.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                items: removeArrayIndex(section.items, itemIndex),
              }
            : section,
        ),
      })),
    );
  }

  function removeIngredientSection(sectionId: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        ingredients: current.ingredients.filter((section) => section.id !== sectionId),
      })),
    );
  }

  function updateDirection(directionId: string, field: "title" | "body" | "detail", value: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        directions: current.directions.map((direction) =>
          direction.id === directionId
            ? {
                ...direction,
                [field]: field === "body" ? value : normalizeNullableValue(value),
              }
            : direction,
        ),
      })),
    );
  }

  function removeDirection(directionId: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        directions: current.directions.filter((direction) => direction.id !== directionId),
      })),
    );
  }

  function addNote(): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        notes: [...current.notes, ""],
      })),
    );
  }

  function updateNote(noteIndex: number, value: string): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        notes: updateArrayIndex(current.notes, noteIndex, value),
      })),
    );
  }

  function removeNote(noteIndex: number): void {
    onChange(
      nextStructuredContent(structuredContent, (current) => ({
        ...current,
        notes: removeArrayIndex(current.notes, noteIndex),
      })),
    );
  }

  return (
    <div className="space-y-3">
      <Card flat>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold">Recipe structure</p>
            <p className="mt-1 text-xs text-[var(--pr-text-muted)]">
              Recipe posts render with a structured detail view instead of the article body layout.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Serving text"
              value={structuredContent.servingText || ""}
              placeholder="Serves 4"
              onChange={(value) => updateTopLevelField("servingText", value)}
            />
            <TextField
              label="Summary title"
              value={structuredContent.summaryTitle || ""}
              placeholder="Why dietitians recommend it"
              onChange={(value) => updateTopLevelField("summaryTitle", value)}
            />
          </div>

          <MultilineField
            label="Summary body"
            value={structuredContent.summaryBody || ""}
            rows={4}
            placeholder="A short dietitian summary shown near the top of the recipe detail view."
            onChange={(value) => updateTopLevelField("summaryBody", value)}
          />

          <MultilineField
            label="Optional intro / fallback body"
            value={body}
            rows={5}
            placeholder="Optional supporting context. If left blank, PantryRx will still save the structured recipe."
            onChange={onBodyChange}
          />
        </div>
      </Card>

      <Card flat>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Stats grid</p>
            <p className="mt-1 text-xs text-[var(--pr-text-muted)]">Use concise values like prep time, protein, or cost cues.</p>
          </div>
          <Button type="button" variant="secondary" className="min-h-10 px-3 text-sm" onClick={() => onChange(addEmptyStat(structuredContent))}>
            Add stat
          </Button>
        </div>

        {structuredContent.stats.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--pr-text-muted)]">Add at least one stat to show a quick reference grid.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {structuredContent.stats.map((stat) => (
              <div key={stat.id} className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface)] p-3">
                <div className="grid gap-3 sm:grid-cols-3">
                  <TextField label="Label" value={stat.label} placeholder="Prep time" onChange={(value) => updateStat(stat.id, "label", value)} />
                  <TextField label="Value" value={stat.value} placeholder="20 min" onChange={(value) => updateStat(stat.id, "value", value)} />
                  <TextField
                    label="Secondary value"
                    value={stat.secondaryValue || ""}
                    placeholder="Budget-friendly"
                    onChange={(value) => updateStat(stat.id, "secondaryValue", value)}
                  />
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeStat(stat.id)}
                    className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
                  >
                    Remove stat
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card flat>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Ingredient sections</p>
            <p className="mt-1 text-xs text-[var(--pr-text-muted)]">Break out sections like base, dressing, or serving options.</p>
          </div>
          <Button
            type="button"
            variant="secondary"
            className="min-h-10 px-3 text-sm"
            onClick={() => onChange(addEmptyIngredientSection(structuredContent))}
          >
            Add section
          </Button>
        </div>

        {structuredContent.ingredients.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--pr-text-muted)]">Add at least one ingredient section with one ingredient line.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {structuredContent.ingredients.map((section, sectionIndex) => (
              <div key={section.id} className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface)] p-3">
                <TextField
                  label={`Section title ${sectionIndex + 1}`}
                  value={section.title || ""}
                  placeholder="Optional section title"
                  onChange={(value) => updateIngredientSectionTitle(section.id, value)}
                />

                <div className="mt-3 space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <label key={`${section.id}-${itemIndex}`} className="flex flex-col gap-2 text-sm">
                      <span className="font-medium text-[var(--pr-text-muted)]">Ingredient line {itemIndex + 1}</span>
                      <div className="flex gap-2">
                        <input
                          value={item}
                          placeholder="1 cup rolled oats"
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            updateIngredientItem(section.id, itemIndex, event.target.value)
                          }
                          className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm outline-none ring-[var(--pr-accent)] transition focus:ring-2"
                        />
                        <button
                          type="button"
                          onClick={() => removeIngredientItem(section.id, itemIndex)}
                          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
                        >
                          Remove
                        </button>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap justify-between gap-2">
                  <Button type="button" variant="secondary" className="min-h-10 px-3 text-sm" onClick={() => addIngredientItem(section.id)}>
                    Add ingredient
                  </Button>
                  <button
                    type="button"
                    onClick={() => removeIngredientSection(section.id)}
                    className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
                  >
                    Remove section
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card flat>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Directions</p>
            <p className="mt-1 text-xs text-[var(--pr-text-muted)]">Add the main step body and optional detail line for timing or cues.</p>
          </div>
          <Button
            type="button"
            variant="secondary"
            className="min-h-10 px-3 text-sm"
            onClick={() => onChange(addEmptyDirection(structuredContent))}
          >
            Add direction
          </Button>
        </div>

        {structuredContent.directions.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--pr-text-muted)]">Add at least one direction so members can follow the recipe.</p>
        ) : (
          <div className="mt-3 space-y-3">
            {structuredContent.directions.map((direction, directionIndex) => (
              <div key={direction.id} className="rounded-2xl border border-[var(--pr-border)] bg-[var(--pr-surface)] p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <TextField
                    label={`Step ${directionIndex + 1} title`}
                    value={direction.title || ""}
                    placeholder="Optional step title"
                    onChange={(value) => updateDirection(direction.id, "title", value)}
                  />
                  <TextField
                    label="Detail"
                    value={direction.detail || ""}
                    placeholder="2 to 3 minutes"
                    onChange={(value) => updateDirection(direction.id, "detail", value)}
                  />
                </div>
                <MultilineField
                  label="Step body"
                  value={direction.body}
                  rows={4}
                  placeholder="Describe the action for this step."
                  onChange={(value) => updateDirection(direction.id, "body", value)}
                />
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeDirection(direction.id)}
                    className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
                  >
                    Remove direction
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card flat>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold">Notes</p>
            <p className="mt-1 text-xs text-[var(--pr-text-muted)]">Use notes for swaps, storage tips, or allergy guidance.</p>
          </div>
          <Button type="button" variant="secondary" className="min-h-10 px-3 text-sm" onClick={addNote}>
            Add note
          </Button>
        </div>

        {structuredContent.notes.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--pr-text-muted)]">Add optional notes for substitutions, storage, or dietitian guidance.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {structuredContent.notes.map((note, noteIndex) => (
              <label key={`note-${noteIndex}`} className="flex flex-col gap-2 text-sm">
                <span className="font-medium text-[var(--pr-text-muted)]">Note {noteIndex + 1}</span>
                <div className="flex gap-2">
                  <input
                    value={note}
                    placeholder="Swap with low-sodium beans if needed."
                    onChange={(event: ChangeEvent<HTMLInputElement>) => updateNote(noteIndex, event.target.value)}
                    className="w-full rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface)] px-3 py-2 text-sm outline-none ring-[var(--pr-accent)] transition focus:ring-2"
                  />
                  <button
                    type="button"
                    onClick={() => removeNote(noteIndex)}
                    className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
                  >
                    Remove
                  </button>
                </div>
              </label>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
